import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo, testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9tm8053pcjqs2fcsgdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description={`${aboutInfo.brandName} - ${aboutInfo.tagline}。${aboutInfo.mission}`}
        keywords="学生的妈妈,关于我们,家庭教育平台,学生家长社区"
        canonicalUrl="https://lijinye.cn/about"
      />
      <SchemaOrg type="WebPage" data={{ name: '关于我们 - 学生的妈妈', description: aboutInfo.mission, url: 'https://lijinye.cn/about' }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://lijinye.cn' }, { '@type': 'ListItem', position: 2, name: '关于我们' }] }} />
      <SchemaOrg type="Organization" data={{ name: aboutInfo.brandName, url: 'https://lijinye.cn', description: aboutInfo.mission, foundingDate: aboutInfo.foundingYear, contactPoint: { '@type': 'ContactPoint', contactType: 'customer service', email: aboutInfo.email, telephone: aboutInfo.phone }, address: { '@type': 'PostalAddress', addressLocality: '北京', addressCountry: 'CN' } }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          {/* Hero */}
          <section className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="mt-8 mb-6">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950">
                关于 <span className="text-primary-500">{aboutInfo.brandName}</span>
              </h1>
              <p className="mt-4 text-lg text-foreground-600 max-w-3xl leading-relaxed">
                {aboutInfo.tagline}
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-lg p-8">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                  <i className="ri-focus-2-line text-xl"></i>
                </span>
                <h2 className="mt-4 text-xl font-heading font-bold text-foreground-950">我们的使命</h2>
                <p className="mt-3 text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-8">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                  <i className="ri-eye-line text-xl"></i>
                </span>
                <h2 className="mt-4 text-xl font-heading font-bold text-foreground-950">我们的愿景</h2>
                <p className="mt-3 text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-8 md:p-10">
              <h2 className="text-2xl font-heading font-bold text-foreground-950">品牌故事</h2>
              <div className="mt-6 space-y-4 text-foreground-600 leading-relaxed">
                <p>
                  <strong>{aboutInfo.brandName}</strong>诞生于{aboutInfo.foundingYear}，由{aboutInfo.founder}共同创立。
                </p>
                <p>
                  我们是一群亲身经历过"陪读焦虑"的妈妈，也有一线教育工作者和心理专家。我们深知，在陪伴孩子学习的路上，妈妈们面临的不只是"怎么教"的技术问题，更有"怎么办"的情绪困扰。信息爆炸的时代让妈妈们被各种相互矛盾的教育理念包围，反而更加迷茫。
                </p>
                <p>
                  于是，我们决定做一件事：用科学的方法、温暖的态度，为每一位学生的妈妈提供真正有用的支持。从学习方法到情绪管理，从亲子沟通到升学规划，我们筛选、验证、分享那些经过实践检验的有效方法，让妈妈们不再孤军奋战。
                </p>
                <p>
                  我们相信，<strong>每一位妈妈都有能力成为孩子最好的支持者</strong>。你不需要成为教育专家，只需要一些恰当的引导和温暖的陪伴——而我们，愿意做你的同行者。
                </p>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              妈妈们的口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5"
                >
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="mt-4 pt-3 border-t border-background-200/70">
                    <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.child}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-950">联系我们</h2>
                <p className="mt-3 text-foreground-600 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或填写右侧表单，我们会尽快回复。
                </p>
                <div className="mt-8 space-y-5">
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">客服电话</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">办公地址</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">发送消息</h3>
                  <form onSubmit={handleContactSubmit} data-readdy-form className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                        您的称呼
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 text-sm outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的称呼"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 text-sm outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 text-sm outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1">{contactForm.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="website-alt-field"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                  {formStatus === 'success' && (
                    <p className="mt-4 text-sm text-primary-600 font-medium text-center">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-4 text-sm text-red-500 font-medium text-center">{formMessage}</p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}